import { Box, ToggleButton, ToggleButtonGroup, Tooltip, Typography, alpha, useTheme } from '@mui/material';
import AutoModeRoundedIcon from '@mui/icons-material/AutoModeRounded';
import CloudRoundedIcon from '@mui/icons-material/CloudRounded';
import CloudOffRoundedIcon from '@mui/icons-material/CloudOffRounded';
import { useEffect, useState } from 'react';
import { conexao } from './conexao';
import { definirModoBusca, inscreverModoBusca, modoBuscaAtual, TModoBusca } from './modoBusca';

const OPCOES: { valor: TModoBusca; label: string; icone: React.ReactNode; descricao: string }[] = [
  {
    valor: 'automatico',
    label: 'Automático',
    icone: <AutoModeRoundedIcon fontSize="small" />,
    descricao: 'Busca no servidor; se a conexão cair, passa a buscar neste computador sozinho (e volta pro servidor quando a conexão voltar).',
  },
  {
    valor: 'online',
    label: 'Online',
    icone: <CloudRoundedIcon fontSize="small" />,
    descricao: 'Sempre busca no servidor. Se a conexão cair, mostra erro em vez de usar dado possivelmente desatualizado.',
  },
  {
    valor: 'offline',
    label: 'Offline',
    icone: <CloudOffRoundedIcon fontSize="small" />,
    descricao: 'Sempre busca na cópia deste computador, mesmo com internet disponível.',
  },
];

// Seletor manual do modo de busca (ver modoBusca.ts) — sobrepõe a detecção
// automática de conexão pra quem faz questão de garantir/forçar um lado.
// Controle segmentado (os três modos à vista, um clique pra trocar) com um
// ponto de status de onde as buscas estão indo AGORA: verde = servidor,
// amarelo = cópia local, vermelho = forçado Online mas sem conexão (as
// buscas vão falhar até a conexão voltar).
export const SeletorModoBusca: React.FC = () => {
  const theme = useTheme();
  const [modo, setModo] = useState<TModoBusca>(modoBuscaAtual());
  const [online, setOnline] = useState(conexao.estaOnline());

  useEffect(() => inscreverModoBusca(setModo), []);
  useEffect(() => conexao.inscrever(setOnline), []);

  const status = modo === 'offline'
    ? { cor: theme.palette.warning.main, texto: 'Buscando neste computador' }
    : online
      ? { cor: theme.palette.success.main, texto: 'Buscando no servidor' }
      : modo === 'online'
        ? { cor: theme.palette.error.main, texto: 'Sem conexão — buscas vão falhar' }
        : { cor: theme.palette.warning.main, texto: 'Sem conexão — buscando neste computador' };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
      <Tooltip title={status.texto}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: status.cor,
              boxShadow: `0 0 0 3px ${alpha(status.cor, 0.2)}`,
              flexShrink: 0,
            }}
          />
          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: { xs: 'none', md: 'block' } }}>
            Busca
          </Typography>
        </Box>
      </Tooltip>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={modo}
        onChange={(_, valor: TModoBusca | null) => { if (valor) definirModoBusca(valor); }}
        aria-label="Modo de busca"
        sx={{
          '& .MuiToggleButton-root': {
            textTransform: 'none',
            gap: 0.5,
            px: 1.25,
            py: 0.4,
            fontSize: '0.75rem',
            fontWeight: 600,
            lineHeight: 1.4,
          },
          '& .MuiToggleButton-root.Mui-selected': {
            bgcolor: alpha(theme.palette.primary.main, 0.12),
            color: 'primary.main',
            '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.18) },
          },
        }}
      >
        {OPCOES.map((opcao) => (
          <Tooltip key={opcao.valor} title={opcao.descricao}>
            <ToggleButton value={opcao.valor} aria-label={opcao.label}>
              {opcao.icone}
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>{opcao.label}</Box>
            </ToggleButton>
          </Tooltip>
        ))}
      </ToggleButtonGroup>
    </Box>
  );
};
